(() => {
  const KEY = "ks_active_promo";
  const CAMPAIGN_KEY = "ks_active_campaign";
  const RULES = window.KS_PROMO_RULES || {};
  const CAMPAIGNS = window.KS_PROMO_CAMPAIGNS || {};

  const normalizeList = (value) => {
    if (Array.isArray(value)) return value.map(s => String(s).trim()).filter(Boolean);
    if (typeof value === "string") return value.split(",").map(s => s.trim()).filter(Boolean);
    return [];
  };

  const findCaseInsensitiveKey = (source, key) => {
    if (!source || typeof source !== "object" || !key) return "";
    if (Object.prototype.hasOwnProperty.call(source, key)) return key;
    const keyUpper = String(key).toUpperCase();
    return Object.keys(source).find(k => String(k || "").toUpperCase() === keyUpper) || "";
  };

  function getPromoRules() {
    return window.KS_PROMO_RULES && typeof window.KS_PROMO_RULES === "object"
      ? window.KS_PROMO_RULES
      : RULES;
  }

  function getPromoCampaigns() {
    return window.KS_PROMO_CAMPAIGNS && typeof window.KS_PROMO_CAMPAIGNS === "object"
      ? window.KS_PROMO_CAMPAIGNS
      : CAMPAIGNS;
  }

  function getCampaignKey(activePromo) {
    return findCaseInsensitiveKey(getPromoCampaigns(), activePromo);
  }

  function isCampaignAlias(activePromo) {
    return !!getCampaignKey(activePromo);
  }

  function getCampaignCodes(activePromo) {
    const campaigns = getPromoCampaigns();
    const key = getCampaignKey(activePromo);
    if (!key) return [];
    const campaign = campaigns[key];
    const codes = Array.isArray(campaign)
      ? campaign
      : Array.isArray(campaign?.codes)
        ? campaign.codes
        : typeof campaign === "string"
          ? campaign
          : [];
    return normalizeList(codes);
  }

  function getCandidateCodes(activePromo) {
    if (isCampaignAlias(activePromo)) return getCampaignCodes(activePromo);
    return activePromo ? [activePromo] : [];
  }

  function getRuleForCode(ruleCode) {
    const rules = getPromoRules();
    const key = findCaseInsensitiveKey(rules, ruleCode);
    return key ? rules[key] : null;
  }

  function normalizePromoInput(value) {
    return String(value || "").split(",")[0].trim();
  }

  function normalizeCode(value) {
    return normalizePromoInput(value).toUpperCase();
  }

  function storageGet(key) {
    try { return (localStorage.getItem(key) || "").trim(); } catch (_) { return ""; }
  }

  function storageSet(key, value) {
    try { localStorage.setItem(key, value); } catch (_) {}
  }

  function storageRemove(key) {
    try { localStorage.removeItem(key); } catch (_) {}
  }

  function persistCampaign(campaignKey) {
    storageSet(KEY, campaignKey);
    storageSet(CAMPAIGN_KEY, campaignKey);
  }

  function persistNormalCode(normalCode) {
    storageSet(KEY, normalCode);
    storageRemove(CAMPAIGN_KEY);
  }

  function campaignContainsCode(campaignKey, value) {
    const normalCode = normalizeCode(value);
    if (!campaignKey || !normalCode) return false;
    return getCampaignCodes(campaignKey)
      .map(code => String(code || "").toUpperCase())
      .includes(normalCode);
  }

  function activatePromo(rawPromo, source) {
    const incoming = normalizePromoInput(rawPromo);
    if (!incoming) return null;

    const campaignKey = getCampaignKey(incoming);
    if (campaignKey) {
      persistCampaign(campaignKey);
      return { value: campaignKey, source, raw: incoming, isCampaign: true };
    }

    const normalCode = incoming.toUpperCase();
    persistNormalCode(normalCode);
    return { value: normalCode, source, raw: incoming, isCampaign: false };
  }

  // When user has a saved promo and clicks a link to the home page (e.g. breadcrumb "Home"
  // which uses shop.url = live store URL), send them to the *current* origin with ?promo=
  // so they stay on the same site (e.g. localhost) and the featured product updates like
  // when using the header "Home page" link.
  document.addEventListener("click", function (e) {
    const a = e.target.closest("a");
    if (!a || !a.href) return;
    try {
      const url = new URL(a.href);
      if (url.pathname !== "/" && url.pathname !== "") return;
      const saved = (localStorage.getItem(KEY) || "").trim();
      if (!saved) return;
      e.preventDefault();
      const home = new URL(location.origin + "/");
      home.searchParams.set("promo", saved);
      location.href = home.toString();
    } catch (_) {}
  }, true);

  // When user clicks an opt-in cart link, send them through the discount URL first
  // so Shopify applies the discount in session (cart + checkout). General `/cart`
  // links (header drawer toggle, etc.) must NOT be intercepted; mark intentional
  // links with data-ks-promo-cart-redirect="true".
  document.addEventListener("click", function (e) {
    const a = e.target.closest("a");
    if (!a || !a.href) return;
    if (a.getAttribute("data-ks-promo-cart-redirect") !== "true") return;
    try {
      const url = new URL(a.href);
      if (url.origin !== location.origin) return;
      const path = (url.pathname || "").replace(/\/+$/, "") || "/";
      if (path !== "/cart") return;
      if (e.defaultPrevented) return;
      const saved = (localStorage.getItem(KEY) || "").trim();
      if (!saved) return;
      if (isCampaignAlias(saved)) return;
      e.preventDefault();
      const discountUrl = new URL(location.origin + "/discount/" + encodeURIComponent(saved));
      discountUrl.searchParams.set("redirect", "/cart");
      location.href = discountUrl.toString();
    } catch (_) {}
  }, true);

  // Debug: add ?ksdebug=1 to the homepage URL
  const DEBUG = new URL(location.href).searchParams.get("ksdebug") === "1";
  const log = (...args) => DEBUG && console.log("[KS PROMO]", ...args);

  function getPromoFromQuery() {
    try {
      const u = new URL(location.href);
      const p = u.searchParams.get("promo");
      if (!p) return null;
      u.searchParams.delete("promo");
      history.replaceState({}, document.title, u.toString());
      return p;
    } catch {
      return null;
    }
  }

  function getPromoFromPath() {
    try {
      const path = location.pathname || "";
      const m = path.match(/^\/discounts?\/([^/?#]+)/i);
      return m ? decodeURIComponent(m[1]) : null;
    } catch {
      return null;
    }
  }

  function getPromoFromReferrer() {
    try {
      const ref = document.referrer;
      if (!ref) return null;
      const u = new URL(ref);
      if (u.origin !== location.origin) return null;
      const m = u.pathname.match(/^\/discounts?\/([^/?#]+)/i);
      return m ? decodeURIComponent(m[1]) : null;
    } catch {
      return null;
    }
  }

  function getPromoFromCookie() {
    const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/i);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }

  const queryPromoRaw = getPromoFromQuery();
  const pathPromoRaw = getPromoFromPath();
  const referrerPromoRaw = getPromoFromReferrer();
  const cookiePromoRaw = getPromoFromCookie();
  const storedPromoBefore = storageGet(KEY);
  const storedCampaignBefore = storageGet(CAMPAIGN_KEY);

  let activePromoState = null;

  if (queryPromoRaw) {
    activePromoState = activatePromo(queryPromoRaw, "query promo");
  } else if (pathPromoRaw) {
    activePromoState = activatePromo(pathPromoRaw, "discount path");
  } else {
    const storedCampaignKey = getCampaignKey(storedCampaignBefore);
    const referrerPromo = normalizePromoInput(referrerPromoRaw);
    const referrerCampaignKey = getCampaignKey(referrerPromo);
    const referrerIsStoredCampaign =
      storedCampaignKey &&
      referrerCampaignKey &&
      referrerCampaignKey.toUpperCase() === storedCampaignKey.toUpperCase();
    const referrerIsCampaignChild = storedCampaignKey && campaignContainsCode(storedCampaignKey, referrerPromo);

    if (storedCampaignKey && (!referrerPromo || referrerIsStoredCampaign || referrerIsCampaignChild)) {
      persistCampaign(storedCampaignKey);
      activePromoState = {
        value: storedCampaignKey,
        source: referrerIsCampaignChild
          ? "stored campaign over child referrer"
          : cookiePromoRaw
            ? "stored campaign over discount cookie"
            : "stored campaign",
        raw: storedCampaignBefore,
        isCampaign: true
      };
    } else if (referrerPromo) {
      activePromoState = activatePromo(referrerPromo, "discount referrer");
    } else if (storedCampaignKey) {
      persistCampaign(storedCampaignKey);
      activePromoState = {
        value: storedCampaignKey,
        source: "stored campaign over discount cookie",
        raw: storedCampaignBefore,
        isCampaign: true
      };
    } else if (cookiePromoRaw) {
      activePromoState = activatePromo(cookiePromoRaw, "discount_code cookie");
    } else {
      const storedPromo = normalizePromoInput(storedPromoBefore);
      if (storedPromo) {
        activePromoState = activatePromo(storedPromo, "stored promo");
      }
    }
  }

  const code = activePromoState ? activePromoState.value : "";

  log("href:", location.href);
  log("referrer:", document.referrer || "(empty)");
  log("explicit promo from URL:", queryPromoRaw || "(none)");
  log("stored ks_active_promo:", storedPromoBefore || "(missing)");
  log("stored ks_active_campaign:", storedCampaignBefore || "(missing)");
  log("discount_code cookie:", cookiePromoRaw || "(missing)");
  log("final active promo source:", activePromoState ? activePromoState.source : "(none)");
  log("final active promo value:", code || "(none)");
  log("active code:", code || "(none)");

  if (!code) return;

  const normalizedCampaignKey = getCampaignKey(code);
  const activeIsCampaign = !!normalizedCampaignKey;
  const campaignCodesResolved = activeIsCampaign ? getCampaignCodes(code) : [];
  const candidateCodes = activeIsCampaign ? campaignCodesResolved : getCandidateCodes(code);

  log("activePromo:", code);
  log("normalized campaign key:", normalizedCampaignKey || "(none)");
  log("campaign codes resolved:", campaignCodesResolved);
  log("candidate codes:", candidateCodes);

  if (!activeIsCampaign && !getRuleForCode(code)) {
    log("No rule found for code:", code, "Available keys:", Object.keys(getPromoRules()));
    return;
  }

  if (activeIsCampaign && candidateCodes.length < 1) {
    log("Campaign alias has no configured codes:", code);
    return;
  }

  log("Candidate codes:", candidateCodes, "Campaign alias:", activeIsCampaign);

  const promoCollectionProductFallbacks = {
    "smoked-seafood": ["smoked-salmon-standard-box", "red-gold-box"]
  };

  function getRuleVariants(rule) {
    return normalizeList(rule && (rule.variants || rule.variant_titles || rule.variant_ids));
  }

  function extractPackSize(value) {
    const text = String(value || "").toLowerCase().replace(/[_-]/g, " ");
    const match = text.match(/\b(4|6|8|12)\b/);
    return match ? match[1] : "";
  }

  function parseVariantList(el) {
    if (!el || !el.getAttribute) return [];
    const raw = el.getAttribute("data-ks-variants");
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function getVariantContext(el) {
    if (!el) return { id: "", title: "", option1: "", packSize: "", list: [] };
    const nestedRoot = el.matches?.("[data-ks-price-root]")
      ? el
      : el.querySelector?.("[data-ks-price-root]");
    const pill = el.querySelector?.("[data-ks-byob-variant-pill].is-active");
    const radio = el.querySelector?.(".ks-box-size-options input[type=radio]:checked");
    const id = (
      el.getAttribute("data-ks-variant-id") ||
      nestedRoot?.getAttribute("data-ks-variant-id") ||
      pill?.getAttribute("data-variant-id") ||
      radio?.getAttribute("data-variant-id") ||
      ""
    ).trim();
    const title = (
      el.getAttribute("data-ks-variant-title") ||
      nestedRoot?.getAttribute("data-ks-variant-title") ||
      pill?.getAttribute("data-variant-title") ||
      radio?.value ||
      ""
    ).trim();
    const list = parseVariantList(el).length ? parseVariantList(el) : parseVariantList(nestedRoot);
    const fromList = id ? list.find((item) => String(item.id) === String(id)) : null;
    const resolvedTitle = title || (fromList && fromList.title) || "";
    const option1 = (fromList && fromList.option1) || resolvedTitle;
    return {
      id,
      title: resolvedTitle,
      option1,
      packSize: extractPackSize(resolvedTitle) || extractPackSize(option1),
      list
    };
  }

  function variantMatchesRule(ctx, allowedVariants) {
    if (!allowedVariants.length) return true;
    if (!ctx) return false;
    const id = String(ctx.id || "").trim();
    const title = String(ctx.title || "").trim().toLowerCase();
    const option1 = String(ctx.option1 || "").trim().toLowerCase();
    const pack = ctx.packSize || extractPackSize(title) || extractPackSize(option1);

    return allowedVariants.some((entry) => {
      const raw = String(entry || "").trim();
      if (!raw) return false;
      if (id && raw === id) return true;
      const lower = raw.toLowerCase();
      if (title && title === lower) return true;
      if (option1 && option1 === lower) return true;
      const entryPack = extractPackSize(raw);
      return !!(entryPack && pack && entryPack === pack);
    });
  }

  function anyListedVariantMatches(list, allowedVariants) {
    if (!allowedVariants.length) return true;
    if (!list.length) return false;
    return list.some((item) => variantMatchesRule({
      id: item.id,
      title: item.title,
      option1: item.option1 || item.title,
      packSize: extractPackSize(item.title) || extractPackSize(item.option1)
    }, allowedVariants));
  }

  function variantsAtPrice(list, cents) {
    return (list || []).filter((item) => Number(item.price) === Number(cents));
  }

  function isPriceVariantEligible(list, cents, allowedVariants) {
    if (!allowedVariants.length) return true;
    const matches = variantsAtPrice(list, cents);
    if (!matches.length) return false;
    return matches.some((item) => variantMatchesRule({
      id: item.id,
      title: item.title,
      option1: item.option1 || item.title,
      packSize: extractPackSize(item.title) || extractPackSize(item.option1)
    }, allowedVariants));
  }

  function getEligibleVariants(list, allowedVariants) {
    if (!Array.isArray(list) || !list.length) return [];
    if (!allowedVariants.length) return list.slice();
    return list.filter((item) => variantMatchesRule({
      id: item.id,
      title: item.title,
      option1: item.option1 || item.title,
      packSize: extractPackSize(item.title) || extractPackSize(item.option1)
    }, allowedVariants));
  }

  function isEligibleForRule(el, rule, variantContext) {
    const allowed = normalizeList(rule && rule.collections);
    const allowedCollections = allowed.map(s => s.toLowerCase());
    const allowedProducts = normalizeList(rule && rule.products).map(s => s.toLowerCase());
    const allowedVariants = getRuleVariants(rule);
    const cols = (el.getAttribute("data-ks-collections") || "")
      .split(",").map(s => s.trim()).filter(Boolean);
    const normalizedCols = cols.map(s => s.toLowerCase());
    const handle = (el.getAttribute("data-ks-product-handle") || "").trim().toLowerCase();
    const ctx = variantContext || getVariantContext(el);

    const hasCollectionRules = allowed.length > 0;
    const hasProductRules = allowedProducts.length > 0;

    let productEligible = true;
    if (hasCollectionRules || hasProductRules) {
      const collectionMatch = hasCollectionRules && allowedCollections.some(h => normalizedCols.includes(h));
      const promoCollectionFallbackMatch = hasCollectionRules && allowedCollections.some(h => {
        const productHandles = promoCollectionProductFallbacks[h] || [];
        return productHandles.includes(handle);
      });
      const productMatch = hasProductRules && allowedProducts.includes(handle);
      productEligible = collectionMatch || promoCollectionFallbackMatch || productMatch;
    }

    let variantEligible = true;
    if (productEligible && allowedVariants.length) {
      if (ctx.id || ctx.title) {
        variantEligible = variantMatchesRule(ctx, allowedVariants);
      } else {
        variantEligible = anyListedVariantMatches(ctx.list, allowedVariants);
      }
    }

    return {
      eligible: productEligible && variantEligible,
      productEligible,
      variantEligible,
      cols,
      handle,
      allowed,
      allowedProducts,
      allowedVariants,
      variant: ctx
    };
  }

  function resolveElementPromo(el, variantContext) {
    for (const candidateCode of candidateCodes) {
      const rule = getRuleForCode(candidateCode);
      if (!rule) continue;
      const eligibility = isEligibleForRule(el, rule, variantContext);
      if (eligibility.eligible) {
        return { code: candidateCode, rule, eligibility };
      }
    }
    return null;
  }

  function variantContextFromItem(item, list) {
    return {
      id: String((item && item.id) || ""),
      title: String((item && item.title) || ""),
      option1: String((item && (item.option1 || item.title)) || ""),
      packSize: extractPackSize(item && item.title) || extractPackSize(item && item.option1),
      list: list || []
    };
  }

  function resolvePromoForPrice(el, variantList, cents) {
    const matches = variantsAtPrice(variantList, cents);
    if (matches.length) {
      for (const item of matches) {
        const resolved = resolveElementPromo(el, variantContextFromItem(item, variantList));
        if (resolved) return resolved;
      }
      return null;
    }
    if (Array.isArray(variantList) && variantList.length) return null;
    return resolveElementPromo(el, { id: "", title: "", option1: "", packSize: "", list: variantList || [] });
  }

  function applyRule(cents, rule) {
    if (rule.type === "PERCENT") return Math.max(0, Math.round(cents * (100 - rule.value) / 100));
    if (rule.type === "AMOUNT") return Math.max(0, cents - rule.value);
    return cents;
  }

  function getProcessedKey(activePromo, resolvedCode, rule, variantId) {
    return [
      activePromo,
      resolvedCode,
      rule && rule.type || "",
      rule && rule.value != null ? rule.value : "",
      normalizeList(rule && rule.collections).join(","),
      normalizeList(rule && rule.products).join(","),
      getRuleVariants(rule).join(","),
      variantId || ""
    ].join("|");
  }

  function clearPromoDecorations(scope) {
    if (!scope) return;
    const nodes = scope.querySelectorAll
      ? [scope, ...scope.querySelectorAll(".ks-sale, .ks-promo-badge, .ks-strike")]
      : [scope];
    nodes.forEach((node) => {
      if (!node || node.nodeType !== 1) return;
      if (node.classList?.contains("ks-sale") || node.classList?.contains("ks-promo-badge")) {
        if (node.parentNode) node.remove();
        return;
      }
      node.classList?.remove("ks-strike");
    });
    scope.querySelectorAll?.(".ks-sale, .ks-promo-badge, .ks-promo-variant-lines").forEach((el) => el.remove());
    scope.querySelectorAll?.(".ks-strike").forEach((el) => el.classList.remove("ks-strike"));
    if (scope.removeAttribute) scope.removeAttribute("data-ks-processed-for");
  }

  const moneyHTML = (amountStr, currency) => {
    const [whole, decRaw = "00"] = amountStr.split(".");
    const dec = (decRaw + "00").slice(0, 2);
    return `<span class="currency-sign">${currency}</span>${whole}<span class="visually-hidden">.</span><sup>${dec}</sup>`;
  };

  const priceTagIcon = '<svg class="ks-promo-badge__icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';

  function makePromoBadge() {
    const span = document.createElement("span");
    span.className = "ks-promo-badge";
    span.setAttribute("aria-label", "Promo Applied");
    span.innerHTML = priceTagIcon + "<em class=\"ks-promo-badge__text\">Promo Applied</em>";
    return span;
  }

  function parseAmountToCents(el) {
    if (!el) return NaN;
    const amtStr = (el.getAttribute("data-amount") || "").replace(/,/g, "");
    if (!amtStr) return NaN;
    const parts = amtStr.split(".");
    const whole = parseInt(parts[0] || "0", 10) || 0;
    const frac = (parts[1] || "00").slice(0, 2);
    return whole * 100 + parseInt(frac.padEnd(2, "0").slice(0, 2), 10);
  }

  function decoratePriceSpan(priceSpan, baseCents, resolved, processedKey, showBadge) {
    if (!priceSpan || !Number.isFinite(baseCents)) return false;
    const discountedCents = applyRule(baseCents, resolved.rule);
    if (discountedCents >= baseCents) return false;

    const parent = priceSpan.parentElement || priceSpan;
    parent.querySelectorAll(".ks-sale").forEach((el) => {
      if (el.previousElementSibling === priceSpan || el === priceSpan.nextElementSibling) el.remove();
    });
    const next = priceSpan.nextElementSibling;
    if (next && (next.classList.contains("ks-sale") || next.classList.contains("ks-promo-badge"))) {
      let node = next;
      while (node && (node.classList.contains("ks-sale") || node.classList.contains("ks-promo-badge"))) {
        const remove = node;
        node = node.nextElementSibling;
        remove.remove();
      }
    }
    priceSpan.classList.remove("ks-strike");

    const currency = priceSpan.getAttribute("data-currency") || "$";
    const baseAmount = (baseCents / 100).toFixed(2);
    const discAmount = (discountedCents / 100).toFixed(2);

    priceSpan.setAttribute("data-ks-processed-for", processedKey);
    priceSpan.classList.add("ks-strike");
    priceSpan.setAttribute("data-amount", baseAmount);
    priceSpan.innerHTML = moneyHTML(baseAmount, currency);

    const sale = document.createElement("span");
    sale.className = (priceSpan.className + " ks-sale").replace("ks-strike", "").replace("price--final-max", "price--final").trim();
    sale.setAttribute("data-currency", currency);
    sale.setAttribute("data-amount", discAmount);
    sale.innerHTML = moneyHTML(discAmount, currency);
    priceSpan.insertAdjacentElement("afterend", sale);

    if (showBadge !== false) {
      const badge = makePromoBadge();
      sale.insertAdjacentElement("afterend", badge);
    }
    return true;
  }

  function appendEligibleVariantPromoLines(tile, variants, resolved, currency) {
    tile.querySelectorAll(".ks-promo-variant-lines").forEach((el) => el.remove());
    if (!variants.length) return false;

    const wrap = document.createElement("div");
    wrap.className = "ks-promo-variant-lines";

    const heading = makePromoBadge();
    const headingText = heading.querySelector(".ks-promo-badge__text");
    if (headingText) headingText.textContent = "Promo";
    heading.setAttribute("aria-label", "Promo");
    wrap.appendChild(heading);

    const seen = new Set();
    variants.forEach((item) => {
      const cents = Number(item.price);
      if (!Number.isFinite(cents) || cents <= 0) return;
      const discounted = applyRule(cents, resolved.rule);
      if (discounted >= cents) return;
      const key = String(item.title || item.option1 || "") + ":" + cents;
      if (seen.has(key)) return;
      seen.add(key);

      const line = document.createElement("p");
      line.className = "ks-promo-variant-line";

      const name = document.createElement("span");
      name.className = "ks-promo-variant-name";
      name.textContent = item.title || item.option1 || "";

      const original = document.createElement("span");
      original.className = "price--final ks-strike";
      original.setAttribute("data-currency", currency);
      original.setAttribute("data-amount", (cents / 100).toFixed(2));
      original.innerHTML = moneyHTML((cents / 100).toFixed(2), currency);

      const sale = document.createElement("span");
      sale.className = "price--final ks-sale";
      sale.setAttribute("data-currency", currency);
      sale.setAttribute("data-amount", (discounted / 100).toFixed(2));
      sale.innerHTML = moneyHTML((discounted / 100).toFixed(2), currency);

      line.appendChild(name);
      line.appendChild(original);
      line.appendChild(sale);
      wrap.appendChild(line);
    });

    if (wrap.querySelectorAll(".ks-promo-variant-line").length < 1) return false;
    tile.appendChild(wrap);
    return true;
  }

  function decorateTile(tile) {
    const productResolved = (() => {
      for (const candidateCode of candidateCodes) {
        const rule = getRuleForCode(candidateCode);
        if (!rule) continue;
        const eligibility = isEligibleForRule(tile, rule, { id: "", title: "", option1: "", packSize: "", list: parseVariantList(tile) });
        if (eligibility.productEligible && (getRuleVariants(rule).length < 1 || eligibility.variantEligible || anyListedVariantMatches(parseVariantList(tile), getRuleVariants(rule)))) {
          return { code: candidateCode, rule, eligibility };
        }
      }
      return null;
    })();

    if (!productResolved) {
      if (tile.hasAttribute("data-ks-featured-tile")) {
        log(
          "Featured product found but no promo matched:",
          "handle:", tile.getAttribute("data-ks-product-handle"),
          "collections:", tile.getAttribute("data-ks-collections"),
          "candidate codes:", candidateCodes
        );
      }
      log("Tile skip (not eligible):", tile.getAttribute("data-ks-product-id"), "candidates:", candidateCodes);
      clearPromoDecorations(tile);
      return false;
    }
    if (tile.hasAttribute("data-ks-featured-tile")) {
      log("Featured product promo resolved:", productResolved.code, productResolved.rule, productResolved.eligibility);
    } else {
      log("Product tile promo resolved:", tile.getAttribute("data-ks-product-handle"), productResolved.code);
    }

    const nestedRoot = tile.querySelector("[data-ks-price-root]");
    const selectedCtx = getVariantContext(nestedRoot || tile);
    const allowedVariants = getRuleVariants(productResolved.rule);
    const variantList = parseVariantList(tile);
    const hasSelectedVariant = !!(selectedCtx.id || selectedCtx.title) && !!nestedRoot;

    const minSpan = tile.querySelector("[data-ks-price-role='min']") || tile.querySelector(".price--final[data-amount][data-currency]");
    if (!minSpan) {
      log("Tile skip (no .price--final):", tile.getAttribute("data-ks-product-id"), "featured:", !!tile.getAttribute("data-ks-featured-tile"));
      return false;
    }

    const displayedMin = parseAmountToCents(minSpan);
    const onSaleEl = tile.querySelector(".on-sale .price--final[data-amount][data-currency]") || tile.querySelector(".price__sale .price--final[data-amount][data-currency]");
    let minCents = Number.isFinite(displayedMin) ? displayedMin : NaN;
    if (!Number.isFinite(minCents) && onSaleEl) minCents = parseAmountToCents(onSaleEl);
    if (!Number.isFinite(minCents)) minCents = parseInt(tile.getAttribute("data-ks-price-min-cents") || "", 10);
    if (!Number.isFinite(minCents)) return false;

    const processedKey = getProcessedKey(
      code,
      productResolved.code,
      productResolved.rule,
      hasSelectedVariant ? selectedCtx.id : "card-eligible"
    );
    const processedFor = tile.getAttribute("data-ks-processed-for");
    if (processedFor === processedKey && (tile.querySelector(".ks-sale") || tile.querySelector(".ks-promo-variant-lines"))) return false;

    tile.querySelectorAll(".ks-sale").forEach(el => el.remove());
    tile.querySelectorAll(".ks-promo-badge").forEach(el => el.remove());
    tile.querySelectorAll(".ks-promo-variant-lines").forEach(el => el.remove());
    tile.querySelectorAll(".ks-strike").forEach(el => el.classList.remove("ks-strike"));

    let changed = false;
    if (hasSelectedVariant) {
      const selectedResolved = resolveElementPromo(nestedRoot || tile, selectedCtx);
      if (selectedResolved) {
        changed = decoratePriceSpan(minSpan, minCents, selectedResolved, processedKey) || changed;
      }
    } else {
      const minResolved = resolvePromoForPrice(tile, variantList, minCents)
        || ((!allowedVariants.length || isPriceVariantEligible(variantList, minCents, allowedVariants)) ? productResolved : null);
      if (minResolved) {
        changed = decoratePriceSpan(minSpan, minCents, minResolved, processedKey, false) || changed;
      }

      const maxSpan = tile.querySelector("[data-ks-price-role='max']");
      const displayedMax = parseAmountToCents(maxSpan);
      const maxCents = Number.isFinite(displayedMax)
        ? displayedMax
        : parseInt(tile.getAttribute("data-ks-price-max-cents") || "", 10);
      if (maxSpan && Number.isFinite(maxCents) && maxCents !== minCents) {
        const maxResolved = resolvePromoForPrice(tile, variantList, maxCents);
        if (maxResolved) {
          changed = decoratePriceSpan(maxSpan, maxCents, maxResolved, processedKey, false) || changed;
        }
      }

      if (changed) {
        const badgeHost = minSpan.parentElement;
        if (badgeHost && !badgeHost.querySelector(".ks-promo-badge")) {
          badgeHost.appendChild(makePromoBadge());
        }
      }
    }

    if (changed) tile.setAttribute("data-ks-processed-for", processedKey);
    else tile.removeAttribute("data-ks-processed-for");
    return changed;
  }

  function decoratePriceRoot(root) {
    const resolved = resolveElementPromo(root);
    if (!resolved) {
      clearPromoDecorations(root);
      return false;
    }
    log("Price root promo resolved:", root.getAttribute("data-ks-product-handle"), resolved.code);

    const ctx = getVariantContext(root);
    const processedKey = getProcessedKey(code, resolved.code, resolved.rule, ctx.id);
    const processedFor = root.getAttribute("data-ks-processed-for");
    if (processedFor === processedKey && root.querySelector(".ks-sale")) return false;

    const displayedCents = parseAmountToCents(root.querySelector(".price--final[data-amount][data-currency]"));
    const baseCents = Number.isFinite(displayedCents)
      ? displayedCents
      : parseInt(root.getAttribute("data-ks-base-cents") || "", 10);
    if (!Number.isFinite(baseCents)) return false;

    const finalPriceEl = root.querySelector(".price--final[data-amount][data-currency]");
    if (!finalPriceEl) return false;

    root.querySelectorAll(".ks-sale").forEach(el => el.remove());
    root.querySelectorAll(".ks-promo-badge").forEach(el => el.remove());
    finalPriceEl.classList.remove("ks-strike");

    const changed = decoratePriceSpan(finalPriceEl, baseCents, resolved, processedKey);
    if (changed) root.setAttribute("data-ks-processed-for", processedKey);
    else root.removeAttribute("data-ks-processed-for");
    return changed;
  }

  function parseInteger(value, fallback = 0) {
    const parsed = parseInt(value || "", 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function hasActualDiscountForCode(line, resolvedCode) {
    const discountText = (line.querySelector(".discounts")?.textContent || "").toUpperCase();
    if (!discountText.trim()) return false;
    return discountText.includes(String(resolvedCode || "").toUpperCase());
  }

  function hasAnyRenderedCartDiscount(line) {
    const discountText = (line.querySelector(".discounts")?.textContent || "").trim();
    return !!discountText || !!line.querySelector(".cart-item__discounted-prices");
  }

  function decorateCartPrice(priceEl, baseCents, discountedCents, processedKey, previewType, showBadge) {
    if (!priceEl || !Number.isFinite(baseCents) || !Number.isFinite(discountedCents)) return false;
    if (discountedCents >= baseCents) return false;

    const scope = priceEl.parentElement || priceEl;
    const existingSale = scope.querySelector(`.ks-sale[data-ks-cart-preview="${previewType}"]`);
    if (priceEl.getAttribute("data-ks-processed-for") === processedKey && existingSale) return false;

    scope.querySelectorAll(`.ks-sale[data-ks-cart-preview="${previewType}"]`).forEach(el => el.remove());
    scope.querySelectorAll(`.ks-promo-badge[data-ks-cart-preview="${previewType}"]`).forEach(el => el.remove());
    priceEl.classList.remove("ks-strike");

    const currency = priceEl.getAttribute("data-currency") || "$";
    const baseAmount = (baseCents / 100).toFixed(2);
    const discountedAmount = (discountedCents / 100).toFixed(2);

    priceEl.setAttribute("data-ks-processed-for", processedKey);
    priceEl.classList.add("ks-strike");
    priceEl.setAttribute("data-amount", baseAmount);
    priceEl.innerHTML = moneyHTML(baseAmount, currency);

    const sale = document.createElement("span");
    sale.className = (priceEl.className + " ks-sale").replace("ks-strike", "").trim();
    sale.setAttribute("data-currency", currency);
    sale.setAttribute("data-amount", discountedAmount);
    sale.setAttribute("data-ks-cart-preview", previewType);
    sale.innerHTML = moneyHTML(discountedAmount, currency);
    priceEl.insertAdjacentElement("afterend", sale);

    if (showBadge) {
      const badge = makePromoBadge();
      badge.setAttribute("data-ks-cart-preview", previewType);
      sale.insertAdjacentElement("afterend", badge);
    }

    return true;
  }

  function decorateCartLine(line) {
    const resolved = resolveElementPromo(line);
    if (!resolved) {
      log("Cart line promo skip:", line.getAttribute("data-ks-product-handle"), "collections:", line.getAttribute("data-ks-collections"), "candidates:", candidateCodes);
      clearPromoDecorations(line);
      return false;
    }

    log("Cart line item promo resolution:", line.getAttribute("data-ks-product-handle"), resolved.code, resolved.eligibility);

    if (hasActualDiscountForCode(line, resolved.code)) {
      log("Cart line already has Shopify discount:", line.getAttribute("data-ks-product-handle"), resolved.code);
      return false;
    }

    if (hasAnyRenderedCartDiscount(line)) {
      log("Cart line has an existing rendered discount; skipping preview:", line.getAttribute("data-ks-product-handle"));
      return false;
    }

    const processedKey = getProcessedKey(code, resolved.code, resolved.rule, line.getAttribute("data-ks-variant-id"));
    const unitBaseCents = parseInteger(line.getAttribute("data-ks-price-cents"), NaN);
    const quantity = Math.max(1, parseInteger(line.getAttribute("data-ks-quantity"), 1));
    if (!Number.isFinite(unitBaseCents)) return false;

    const unitDiscountedCents = applyRule(unitBaseCents, resolved.rule);
    let changed = false;

    const unitPriceEl =
      line.querySelector(".cart-item__price .ks-cart-unit-price.price--final[data-amount][data-currency]") ||
      line.querySelector(".cart-item__price .price--final[data-amount][data-currency]");
    if (unitPriceEl) {
      changed = decorateCartPrice(unitPriceEl, unitBaseCents, unitDiscountedCents, processedKey, "unit", true) || changed;
    }

    const linePriceEl = line.querySelector(".cart-item__totals .ks-cart-line-price[data-amount][data-currency]");
    if (linePriceEl) {
      const lineBaseCents = parseInteger(line.getAttribute("data-ks-line-price-cents"), unitBaseCents * quantity);
      const lineDiscountedCents = unitDiscountedCents * quantity;
      changed = decorateCartPrice(linePriceEl, lineBaseCents, lineDiscountedCents, processedKey, "line", false) || changed;
    }

    if (changed) line.setAttribute("data-ks-processed-for", processedKey);
    return changed;
  }

  function decorateCartLines() {
    const cartLines = Array.from(document.querySelectorAll("[data-ks-cart-line]"));
    let changed = 0;
    cartLines.forEach(line => {
      if (decorateCartLine(line)) changed++;
    });
    log("Cart lines found:", cartLines.length, "Cart lines changed:", changed);
    return changed;
  }

  function decorateVariantPriceRoots() {
    const cards = Array.from(document.querySelectorAll("[data-ks-variant-price-root]"));
    let changed = 0;
    cards.forEach((card) => {
      const resolved = resolveElementPromo(card);
      const priceSpan = card.querySelector(".price--final[data-amount][data-currency]") || card.querySelector(".ks-box-size-option-card__price");
      if (!resolved) {
        clearPromoDecorations(card);
        return;
      }
      const ctx = getVariantContext(card);
      const processedKey = getProcessedKey(code, resolved.code, resolved.rule, ctx.id);
      if (card.getAttribute("data-ks-processed-for") === processedKey && card.querySelector(".ks-sale")) return;
      const baseCents = parseInteger(card.getAttribute("data-ks-base-cents"), parseAmountToCents(priceSpan));
      if (!priceSpan || !Number.isFinite(baseCents)) return;
      card.querySelectorAll(".ks-sale, .ks-promo-badge").forEach((el) => el.remove());
      priceSpan.classList.remove("ks-strike");
      if (decoratePriceSpan(priceSpan, baseCents, resolved, processedKey)) {
        card.setAttribute("data-ks-processed-for", processedKey);
        changed++;
      }
    });
    return changed;
  }

  function formatPromoMoney(cents, priceEl) {
    const iso = (priceEl?.getAttribute("data-currency") || "").trim();
    if (iso.length === 3 && typeof Intl !== "undefined") {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en", {
          style: "currency",
          currency: iso
        }).format(cents / 100);
      } catch (_) {}
    }
    const sample = priceEl?.getAttribute("data-money-sample") || "";
    const symbol = sample.replace(/[\d.,\s]/g, "").trim() || "$";
    const amount = (cents / 100).toFixed(2);
    return symbol + amount;
  }

  function decorateByob(root) {
    if (!root) return 0;
    const pill = root.querySelector("[data-ks-byob-variant-pill].is-active");
    if (pill) {
      root.setAttribute("data-ks-variant-id", pill.getAttribute("data-variant-id") || "");
      root.setAttribute("data-ks-variant-title", pill.getAttribute("data-variant-title") || "");
    }
    const resolved = resolveElementPromo(root);
    const priceEls = Array.from(root.querySelectorAll("[data-ks-byob-price]"));
    let changed = 0;
    priceEls.forEach((priceEl) => {
      const parent = priceEl.parentElement || priceEl;
      parent.querySelectorAll(":scope > .ks-sale, :scope > .ks-promo-badge").forEach((el) => el.remove());
      let node = priceEl.nextElementSibling;
      while (node && (node.classList.contains("ks-sale") || node.classList.contains("ks-promo-badge"))) {
        const remove = node;
        node = node.nextElementSibling;
        remove.remove();
      }
      priceEl.classList.remove("ks-strike");
      if (!resolved) {
        priceEl.removeAttribute("data-ks-processed-for");
        return;
      }
      const baseCents = parseInteger(priceEl.getAttribute("data-ks-base-cents"), NaN);
      if (!Number.isFinite(baseCents) || baseCents <= 0) return;
      const discountedCents = applyRule(baseCents, resolved.rule);
      if (discountedCents >= baseCents) return;
      const ctx = getVariantContext(root);
      const processedKey = getProcessedKey(code, resolved.code, resolved.rule, ctx.id);
      if (priceEl.getAttribute("data-ks-processed-for") === processedKey && priceEl.classList.contains("ks-strike")) {
        changed++;
        return;
      }
      priceEl.setAttribute("data-ks-processed-for", processedKey);
      priceEl.classList.add("ks-strike");
      const sale = document.createElement("span");
      sale.className = "ks-byob__price-amount ks-sale";
      sale.setAttribute("data-ks-cart-preview", "byob");
      sale.textContent = formatPromoMoney(discountedCents, priceEl);
      priceEl.insertAdjacentElement("afterend", sale);
      const badge = makePromoBadge();
      badge.setAttribute("data-ks-cart-preview", "byob");
      sale.insertAdjacentElement("afterend", badge);
      changed++;
    });
    if (!resolved) root.removeAttribute("data-ks-processed-for");
    return changed;
  }

  function resolveCampaignCodesFromCartLines() {
    if (!activeIsCampaign) return [];

    const configuredCodes = getCampaignCodes(code);
    log("Campaign checkout using full configured code list:", configuredCodes);
    return configuredCodes;
  }

  function getCartUpdateUrl() {
    if (window.routes && window.routes.cart_update_url) return window.routes.cart_update_url;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return root.replace(/\/$/, "") + "/cart/update.js";
  }

  function isCartCheckoutSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== "FORM") return false;

    const submitter = event.submitter;
    const hasCheckoutSubmitter =
      submitter && submitter.name === "checkout" ||
      form.querySelector('[name="checkout"]');
    if (!hasCheckoutSubmitter) return false;

    try {
      const action = new URL(form.action || location.href, location.origin);
      const cartPath = (window.routes && window.routes.cart_url || "/cart").replace(/\/+$/, "") || "/cart";
      const actionPath = (action.pathname || "").replace(/\/+$/, "") || "/";
      return actionPath === cartPath || actionPath === "/cart";
    } catch (_) {
      return false;
    }
  }

  async function applyCampaignDiscountsToCart() {
    if (!activeIsCampaign) return false;
    const campaignCodes = resolveCampaignCodesFromCartLines();
    if (campaignCodes.length < 1) {
      log("No eligible campaign discounts to post for cart.");
      return false;
    }

    const discount = campaignCodes.join(",");
    log("checkout discount string sent:", discount);

    const response = await fetch(getCartUpdateUrl(), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({ discount })
    });

    log("cart/update.js response status:", response.status);

    if (!response.ok) {
      throw new Error("cart/update discount failed: " + response.status);
    }

    return true;
  }

  function continueCheckoutSubmit(form, submitter) {
    form.setAttribute("data-ks-promo-campaign-submitting", "1");
    log("checkout submit resumes after campaign update");

    if (typeof form.requestSubmit === "function" && submitter) {
      form.requestSubmit(submitter);
      return;
    }

    let hidden = null;
    if (!submitter || submitter.name !== "checkout") {
      hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "checkout";
      hidden.value = "checkout";
      form.appendChild(hidden);
    }

    HTMLFormElement.prototype.submit.call(form);
  }

  function bindCampaignCheckoutSubmit() {
    if (!activeIsCampaign) return;

    document.addEventListener("submit", function (event) {
      if (!isCartCheckoutSubmit(event)) return;

      const form = event.target;
      if (form.getAttribute("data-ks-promo-campaign-submitting") === "1") return;

      event.preventDefault();
      log("campaign checkout submit intercepted:", code, getCampaignCodes(code));

      if (form.getAttribute("data-ks-promo-campaign-applying") === "1") return;
      form.setAttribute("data-ks-promo-campaign-applying", "1");

      const submitter = event.submitter || form.querySelector('[name="checkout"]');
      applyCampaignDiscountsToCart()
        .catch((error) => {
          log("Campaign discount apply failed; continuing checkout:", error);
        })
        .finally(() => {
          form.removeAttribute("data-ks-promo-campaign-applying");
          continueCheckoutSubmit(form, submitter);
        });
    }, true);
  }

  function syncSimpleBundlesVariant() {
    const island = document.querySelector("[data-ks-byob-promo-product]");
    const bundle = document.querySelector("byob-bundle");
    if (!island || !bundle) return false;
    const selectedId = bundle.getAttribute("selected-variant-id") || "";
    const pack = selectedId ? bundle.querySelector(`byob-pack[variant-id="${selectedId}"]`) : null;
    const selectedTitle = pack ? pack.getAttribute("label") || "" : "";
    if (selectedId) island.setAttribute("data-ks-variant-id", selectedId);
    if (selectedTitle) island.setAttribute("data-ks-variant-title", selectedTitle);
    const list = parseVariantList(island);
    const selected = selectedId
      ? list.find((item) => String(item.id) === String(selectedId))
      : null;
    const islandHandle = (island.getAttribute("data-ks-product-handle") || "").trim();
    document.querySelectorAll("[data-ks-price-root]").forEach((root) => {
      if (islandHandle && root.getAttribute("data-ks-product-handle") !== islandHandle) return;
      if (selectedId) {
        root.setAttribute("data-ks-variant-id", selectedId);
        if (selectedTitle) root.setAttribute("data-ks-variant-title", selectedTitle);
      }
      if (selected && Number.isFinite(Number(selected.price))) {
        root.setAttribute("data-ks-base-cents", String(selected.price));
        const priceEl = root.querySelector(".price--final[data-amount][data-currency]");
        if (priceEl) priceEl.setAttribute("data-amount", (Number(selected.price) / 100).toFixed(2));
      }
    });
    return true;
  }

  function run() {
    syncSimpleBundlesVariant();
    const tiles = Array.from(document.querySelectorAll("[data-ks-tile]"));
    let tilesChanged = 0;
    tiles.forEach(t => { if (decorateTile(t)) tilesChanged++; });

    const roots = Array.from(document.querySelectorAll("[data-ks-price-root]"));
    let rootsChanged = 0;
    roots.forEach(r => {
      if (r.closest("[data-ks-tile]")) return;
      if (r.closest("[data-ks-variant-price-root]")) return;
      if (decoratePriceRoot(r)) rootsChanged++;
    });
    const variantCardsChanged = decorateVariantPriceRoots();
    let byobChanged = 0;
    document.querySelectorAll("[data-ks-byob-promo], [data-ks-byob]").forEach((root) => {
      byobChanged += decorateByob(root);
    });
    const cartLinesChanged = decorateCartLines();

    log("Tiles found:", tiles.length, "Tiles changed:", tilesChanged, "Price roots:", roots.length, "Roots changed:", rootsChanged, "Variant cards:", variantCardsChanged, "BYOB:", byobChanged, "Cart lines changed:", cartLinesChanged);
  }

  window.KSPromoPreview = {
    run,
    decorateByob
  };

  bindCampaignCheckoutSubmit();

  // Run once after DOM parse (script is defer, but safe anyway)
  run();

  // Run again after a short delay to catch tabs/sliders that render content late
  setTimeout(run, 400);
  setTimeout(run, 1200);

  // Extra delayed runs: home page (featured product section may load late), product page (theme may re-render price)
  const path = (location.pathname || "").replace(/^\//, "") || "index";
  const isHome = path === "" || path === "index";
  const isProduct = path.startsWith("products/");
  if (isHome) {
    log("Home page: watching for tiles + featured product (filter console by 'KS PROMO' to see only these logs)");
    setTimeout(run, 2000);
    setTimeout(run, 3500);
    setTimeout(run, 4500);
    setTimeout(run, 6000);
    for (let i = 1; i <= 10; i++) setTimeout(run, 1500 * i);
    // Dedicated poll for featured-product section (loads late or in different order)
    let featuredPollCount = 0;
    const featuredPoll = setInterval(() => {
      featuredPollCount++;
      const featured = document.querySelector("[data-ks-featured-tile]");
      if (featured) {
        if (featured.querySelector(".ks-sale")) {
          if (featuredPollCount === 1) log("Featured tile already has promo");
        } else if (decorateTile(featured)) {
          log("Featured tile APPLIED on poll #", featuredPollCount);
        } else {
          if (featuredPollCount <= 3) log("Featured tile found but decorateTile returned false (see Tile skip above)");
        }
      }
      if (featuredPollCount >= 40) clearInterval(featuredPoll);
    }, 500);
  }
  if (isProduct) {
    setTimeout(run, 1500);
    setTimeout(run, 2500);
    setTimeout(run, 4000);
    setTimeout(run, 6000);
    if (/build_your-own_box/i.test(path)) {
      for (let i = 1; i <= 8; i++) setTimeout(run, 1000 * i);
    }
  }

  // Re-run when page is shown (e.g. back/forward, or when breadcrumb "Home" does client-side nav)
  window.addEventListener("pageshow", () => {
    setTimeout(run, 100);
    setTimeout(run, 800);
    const p = (location.pathname || "").replace(/^\//, "") || "index";
    if (p === "" || p === "index") {
      setTimeout(run, 2000);
      setTimeout(run, 3500);
      setTimeout(run, 5000);
      let n = 0;
      const id = setInterval(() => {
        n++;
        const featured = document.querySelector("[data-ks-featured-tile]");
        if (featured && !featured.querySelector(".ks-sale") && decorateTile(featured)) {
          clearInterval(id);
        }
        if (n >= 40) clearInterval(id);
      }, 500);
    }
    if (p.startsWith("products/")) {
      setTimeout(run, 2000);
      setTimeout(run, 4000);
    }
  });

  // Re-run on Turbo Drive page load (some Shopify themes use Turbo for in-page navigation)
  document.addEventListener("turbo:load", () => {
    setTimeout(run, 100);
    setTimeout(run, 800);
    const p = (location.pathname || "").replace(/^\//, "") || "index";
    if (p === "" || p === "index") {
      setTimeout(run, 2000);
      setTimeout(run, 5000);
    }
    if (p.startsWith("products/")) {
      setTimeout(run, 2000);
      setTimeout(run, 4000);
    }
  });

  // When a price block becomes visible (e.g. featured product section below fold), run again
  try {
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        setTimeout(run, 150);
      },
      { root: null, rootMargin: "100px", threshold: 0.01 }
    );
    const observeTargets = () => {
      document.querySelectorAll("[data-ks-tile], [data-ks-price-root], [data-ks-variant-price-root], [data-ks-byob], [data-ks-byob-price]").forEach((el) => {
        if (!el.hasAttribute("data-ks-observed")) {
          el.setAttribute("data-ks-observed", "1");
          io.observe(el);
        }
      });
    };
    observeTargets();
    setTimeout(observeTargets, 500);
    setTimeout(observeTargets, 2000);
  } catch (_) {}

  // Debounced observer for lazy-loaded sections
  let timer = null;
  const obs = new MutationObserver((mutations) => {
    if (DEBUG) {
      const cartMutation = mutations.some((mutation) =>
        Array.from(mutation.addedNodes || []).some((node) => {
          if (!node || node.nodeType !== 1) return false;
          return (
            node.matches?.("[data-ks-cart-line], #cart-sidebar-items, #cart-sidebar-footer, #main-cart-items") ||
            node.querySelector?.("[data-ks-cart-line], #cart-sidebar-items, #cart-sidebar-footer, #main-cart-items")
          );
        })
      );
      if (cartMutation) log("cart drawer/page rerender detected");
    }
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      run();
    }, 250);
  });
  obs.observe(document.documentElement, { childList: true, subtree: true });

  function bindByobBundleObserver() {
    const bundle = document.querySelector("byob-bundle");
    if (!bundle || bundle.getAttribute("data-ks-promo-observed") === "1") return;
    bundle.setAttribute("data-ks-promo-observed", "1");
    const attrObs = new MutationObserver(() => setTimeout(run, 50));
    attrObs.observe(bundle, { attributes: true, attributeFilter: ["selected-variant-id"] });
    log("Watching byob-bundle selected-variant-id");
  }
  bindByobBundleObserver();
  setTimeout(bindByobBundleObserver, 800);
  setTimeout(bindByobBundleObserver, 2000);
  setTimeout(bindByobBundleObserver, 4500);
})();
